import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { whiteboardModeChanged } from 'features/whiteboard/actions';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import {
  selectCanManagePrivateWhiteboards,
  selectRolesWithPrivateWhiteboardPermission,
} from 'features/permissions/permissionsSlice';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { FormControlLabel, Checkbox } from '@mui/material';
import { useDontShowAgain } from 'hooks/useDontShowAgain';

const MakeWhiteboardPublicConfirmationModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalData = useAppSelector(selectActiveModalData);
  const whiteboardId = modalData.whiteboardId as string;

  const canManagePrivateWhiteboard = useAppSelector(selectCanManagePrivateWhiteboards);
  const rolesWithPrivateWhiteboardPermission = useAppSelector(
    selectRolesWithPrivateWhiteboardPermission
  );

  const confirm = () => {
    dispatch(whiteboardModeChanged({ id: whiteboardId, private: false }));
    dispatch(closeModal('makeWhiteboardPublicConfirmation'));
  };

  const { dontShowAgain, handleChange, savePreference } = useDontShowAgain({
    modalName: 'makeWhiteboardPublicConfirmation',
    onConfirm: confirm,
  });

  const shouldClose = !canManagePrivateWhiteboard;

  useEffect(() => {
    if (shouldClose) {
      dispatch(closeModal('makeWhiteboardPublicConfirmation'));
    }
  }, [shouldClose, dispatch]);

  const handleConfirm = () => {
    savePreference();
    confirm();
  };

  const handleCancel = () => {
    dispatch(closeModal('makeWhiteboardPublicConfirmation'));
  };

  const renderDescription = () => {
    const rolesList = rolesWithPrivateWhiteboardPermission.join(', ');

    return t('make_whiteboard_public_confirmation.description', { roles: rolesList });
  };

  return (
    <ConfirmationDialog
      open={!shouldClose}
      hideCloseButton
      preventClose
      title={t('make_whiteboard_public_confirmation.title')}
      description={renderDescription()}
      okText={t('make_whiteboard_public_confirmation.btn_ok_label')}
      onOk={handleConfirm}
      cancelText={t('make_whiteboard_public_confirmation.btn_cancel_label')}
      onCancel={handleCancel}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={dontShowAgain}
            onChange={(e) => handleChange(e.target.checked)}
            color="primary"
          />
        }
        label={t('make_whiteboard_public_confirmation.dont_show_again')}
        sx={{ mt: 2 }}
      />
    </ConfirmationDialog>
  );
};

export default MakeWhiteboardPublicConfirmationModal;
